/**
 * Leitura automática do recorte em tela.
 *
 * Recalcula a cada mudança de filtro, a partir dos casos carregados — nenhum texto
 * aqui é fixo, e nenhum número é estimado: tudo sai da contagem dos próprios casos.
 * Quando o Hermes estiver ligado ao domínio `reclamacoes`, estes cards passam a ser
 * a entrada do modelo (o que a tela já sabe), não o seu substituto.
 */

import { agregarPorItem, brl0, type CasoMl } from './logic'

export type Insight = {
  titulo: string
  detalhe: string
  tom: 'critico' | 'atencao' | 'neutro'
}

const pct = (parte: number, total: number) => (total ? Math.round((parte / total) * 100) : 0)

export function lerRecorte(casos: CasoMl[]): Insight[] {
  if (!casos.length) return []

  const out: Insight[] = []
  const { ranking, familias } = agregarPorItem(casos)
  const total = casos.length

  // 1. concentração por anúncio
  if (ranking.length >= 2) {
    const [a, b] = ranking
    const juntos = a.n + b.n
    out.push({
      titulo: `Os 2 anúncios mais reclamados carregam ${pct(juntos, total)}% da fila`,
      detalhe:
        `${a.titulo} (${a.mlb}) com ${a.n} e ${b.titulo} (${b.mlb}) com ${b.n} — ` +
        `${juntos} de ${total} casos, ${brl0(a.valor + b.valor)} em pedidos reclamados.`,
      tom: pct(juntos, total) >= 10 ? 'critico' : 'neutro',
    })
  }

  // 2. janelas perdidas
  const comPrazo = casos.filter((c) => c.temPrazo)
  const perdidas = comPrazo.filter((c) => c.noPrazo === 0)
  if (perdidas.length) {
    const semNinguem = perdidas.filter((c) => c.mensagens === 0).length
    out.push({
      titulo: `${perdidas.length} janela${perdidas.length > 1 ? 's' : ''} de prazo perdida${perdidas.length > 1 ? 's' : ''}`,
      detalhe:
        `De ${comPrazo.length} casos em que o mediador escreveu prazo, ${perdidas.length} ` +
        `${perdidas.length === 1 ? 'venceu' : 'venceram'} sem resposta` +
        (semNinguem ? ` — em ${semNinguem} ninguém chegou a abrir o caso.` : '.') +
        ' Vendedor que não responde no prazo desde o primeiro contato é motivo que o ML considera válido.',
      tom: 'critico',
    })
  }

  // 3. motivo dominante
  const porMotivo = new Map<string, { n: number; hot: boolean }>()
  for (const c of casos) {
    const cur = porMotivo.get(c.motivoLabel) || { n: 0, hot: false }
    cur.n += 1
    cur.hot = cur.hot || c.hot
    porMotivo.set(c.motivoLabel, cur)
  }
  const [motivoTop] = [...porMotivo.entries()].sort((a, b) => b[1].n - a[1].n)
  if (motivoTop) {
    out.push({
      titulo: `"${motivoTop[0]}" é ${pct(motivoTop[1].n, total)}% do recorte`,
      detalhe: motivoTop[1].hot
        ? `${motivoTop[1].n} casos. É motivo que o ML trata como válido e não excluível — o ganho aqui vem de consertar a causa, não de contestar.`
        : `${motivoTop[1].n} casos. Não está na lista de motivos automaticamente válidos: vale checar caso a caso se cabe pedido de exclusão.`,
      tom: motivoTop[1].hot ? 'atencao' : 'neutro',
    })
  }

  // 4. dinheiro parado nos casos abertos
  const abertas = casos.filter((c) => c.aberta)
  if (abertas.length) {
    const valor = abertas.reduce((s, c) => s + (c.valor || 0), 0)
    out.push({
      titulo: `${abertas.length} casos ainda abertos, ${brl0(valor)} em jogo`,
      detalhe:
        'Soma dos pedidos reclamados que seguem sem desfecho. O ML não analisa exclusão de reclamação ' +
        'em aberto — resolver é pré-requisito para qualquer contestação depois.',
      tom: abertas.length > 20 ? 'atencao' : 'neutro',
    })
  }

  // 5. família que mais produz caso
  if (familias.length) {
    const f = familias[0]
    out.push({
      titulo: `${f.nome} é a família que mais produz reclamação`,
      detalhe:
        `${f.n} casos em ${f.anuncios} anúncio${f.anuncios > 1 ? 's' : ''}, ` +
        `${brl0(f.valor / f.n)} por caso. ` +
        (f.natureza.g1 > f.n / 2
          ? 'A maioria é problema que aparece depois que o produto chega — olhar lote e embalagem.'
          : f.natureza.g3 > f.n / 2
            ? 'A maioria é entrega e endereço — olhar expedição, não o produto.'
            : 'Natureza mista: ver a divisão na barra da família.'),
      tom: 'neutro',
    })
  }

  return out
}
